import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { CampaignFormFields } from '@/campaigns/campaign-form';
import {
  useCampaigns,
  useCreateCampaign,
  type CampaignRecord,
  type CampaignSort,
  type CampaignsListFilter,
} from '@/campaigns/queries';
import {
  CAMPAIGN_STATUSES,
  CampaignCreateSchema,
  type CampaignCreateInput,
  type CampaignStatus,
} from '@/campaigns/schemas';
import { useClients } from '@/clients/queries';
import { DataTable } from '@/components/data-table';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListQueryRoute } from '@/lib/use-list-query-route';
import { useWarehouses } from '@/warehouses/queries';

const ALL = '__all__';

export const CampaignsListPage = () => {
  // Phase 8.5-05 — URL-driven list state (ADR-014). Filter / sort / page
  // round-trip through the URL bar so deep-links are share-able and the
  // back button restores the previous view.
  const lq = useListQueryRoute<CampaignsListFilter, CampaignSort>({
    from: '/_protected/campaigns',
    defaultFilter: {},
    defaultSort: { field: 'createdAt', dir: 'desc' },
  });
  const [createOpen, setCreateOpen] = useState(false);

  const campaignsQuery = useCampaigns({
    filter: lq.filter,
    sort: lq.sort,
    page: lq.page,
    pageSize: lq.pageSize,
  });
  const { data: clients } = useClients();
  const { data: warehouses } = useWarehouses();
  const clientsNameById = new Map((clients ?? []).map((c) => [c.id, c.name]));
  const warehousesNameById = new Map((warehouses ?? []).map((w) => [w.id, w.name]));

  const createCampaign = useCreateCampaign();

  const items = campaignsQuery.data?.items ?? [];
  const total = campaignsQuery.data?.total ?? 0;

  const columns: ColumnDef<CampaignRecord>[] = [
    {
      id: 'name',
      header: 'Название',
      meta: { sortField: 'name' },
      cell: ({ row }) => (
        <Link
          to="/campaigns/$campaignId"
          params={{ campaignId: row.original.id }}
          className="font-medium hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: 'client',
      header: 'Клиент',
      cell: ({ row }) => clientsNameById.get(row.original.clientId) ?? row.original.clientId,
    },
    {
      id: 'warehouse',
      header: 'Склад',
      cell: ({ row }) =>
        warehousesNameById.get(row.original.warehouseId) ?? row.original.warehouseId,
    },
    {
      id: 'mode',
      header: 'Режим',
      cell: ({ row }) => <Badge variant="outline">{row.original.itemSelectionMode}</Badge>,
    },
    {
      id: 'status',
      header: 'Статус',
      cell: ({ row }) => <StatusDot domain="campaign" state={row.original.status} />,
    },
    {
      id: 'createdAt',
      header: 'Создана',
      meta: { sortField: 'createdAt' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString('ru-RU')}
        </span>
      ),
    },
  ];

  const handleSubmit = (values: CampaignCreateInput): void => {
    createCampaign.mutate(values, {
      onSuccess: () => {
        toast.success('Кампания создана');
        setCreateOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const filterSlot = (
    <>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={lq.filter.includeDeleted ?? false}
          onChange={(e) => {
            lq.updateQuery({
              filter: { includeDeleted: e.target.checked ? true : undefined },
            });
          }}
          className="h-4 w-4"
        />
        Показывать удалённых
      </label>
      <div className="w-40">
        <Select
          value={lq.filter.clientId ?? ALL}
          onValueChange={(v) => {
            lq.updateQuery({ filter: { clientId: v === ALL ? undefined : v } });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все клиенты</SelectItem>
            {(clients ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-40">
        <Select
          value={lq.filter.status ?? ALL}
          onValueChange={(v) => {
            lq.updateQuery({
              filter: { status: v === ALL ? undefined : (v as CampaignStatus) },
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все статусы</SelectItem>
            {CAMPAIGN_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Кампании"
        actions={
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Создать кампанию
          </Button>
        }
      >
        {campaignsQuery.isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Загрузка…</div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            emptyMessage="Нет кампаний"
            filterSlot={filterSlot}
            sort={{ field: lq.sort.field, dir: lq.sort.dir }}
            onSortChange={(next) => {
              lq.updateQuery({
                sort: { field: next.field as CampaignSort | null, dir: next.dir },
              });
            }}
            pagination={{
              page: lq.page,
              pageSize: lq.pageSize,
              total,
              onPageChange: (page) => {
                lq.updateQuery({ page });
              },
            }}
          />
        )}
      </PageShell>

      <FormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Новая кампания"
        schema={CampaignCreateSchema}
        defaultValues={{
          clientId: '',
          warehouseId: '',
          name: '',
          messageText: '',
          itemSelectionMode: 'manual',
        }}
        onSubmit={handleSubmit}
        isPending={createCampaign.isPending}
      >
        <CampaignFormFields mode="create" />
      </FormDialog>
    </div>
  );
};
